import React from 'react';
import { Link } from '../../widgets/buttons';
import styled from 'styled-components';
import { ErrorCallout, ErrorCalloutTitle } from '../../widgets/errorCallout/errorCallout';

const ErrorContainer = styled.div`
    display: flex;
    white-space: pre-wrap;
    align-content: start;
`;

const StyledErrorCallout = styled(ErrorCallout)`
    align-self: center;
    margin-bottom: 12px;
`;

const ErrorTitle = styled(ErrorCalloutTitle)`
    margin-bottom: 16px;
`;

const StrongText = styled.span`
    font-weight: bold;
`;

const Placeholder = styled.span`
    font-weight: bold;
`;

const LineBreak = styled.div`
    height: 1px;
    margin: 6px 0;
    display: block;
`;

interface OrderErrorRenderingProps {
    message: string;
    designId?: number;
    designDocId?: string;
    supportEmail?: string;
    isWarningMessage?: boolean;
}

const DEFAULT_SUPPORT_EMAIL = 'help@zakeke.com';

const OrderErrorRendering: React.FC<OrderErrorRenderingProps> = ({
    message,
    designId,
    designDocId,
    supportEmail = DEFAULT_SUPPORT_EMAIL,
    isWarningMessage = false
}) => {
    const ERROR_GUIDE_LINKS: Record<string, string> = {
        '101': 'https://zakeke.zendesk.com/hc/en-us/articles/22995675178524-Orders-Error-Guide#h_01K7PJPBKMJVR2Y0J1V959NG1B',
        '102': 'https://zakeke.zendesk.com/hc/en-us/articles/22995675178524-Orders-Error-Guide#h_01K7PJPBKPJSG787013TVST1RK',
        '103.1': 'https://zakeke.zendesk.com/hc/en-us/articles/22995675178524-Orders-Error-Guide#h_01K7PJPBKY2947DT1QXFX6DX6R',
        '103.2': 'https://zakeke.zendesk.com/hc/en-us/articles/22995675178524-Orders-Error-Guide#h_01K7PJPBM2202W4ZP55XZDC8EX',
        '104': 'https://zakeke.zendesk.com/hc/en-us/articles/22995675178524-Orders-Error-Guide#h_01K7PJPBM5WTCGXWMGY495BSJX',
        '105': 'https://zakeke.zendesk.com/hc/en-us/articles/22995675178524-Orders-Error-Guide#h_01K7PJPBM8VM1H6DQKWQJK86S3'
    };

    let errorCode = '';

    const associateGuideSection = (errorCode: string) => {
        const guideLink = errorCode ? ERROR_GUIDE_LINKS[errorCode] : '';
        return guideLink;
    };

    // Replace placeholders
    let formattedMessage = message
        .replace(/{supportEmail}/g, '__SUPPORT_EMAIL__')
        .replace(/{designID}/g, designId ? '__DESIGN_ID__' : '')
        .replace(/{designDocID}/g, designDocId ? '__DESIGN_DOC_ID__' : '');

    //Replace tag with referring components
    const renderWithTags = (text: string) => {
        const regex =
            /(<title>.*?<\/title>)|(<strong>.*?<\/strong>)|(<a(?:\s[^>]*)?>.*?<\/a>)|(<br\s*\/?>)|(__SUPPORT_EMAIL__)|(__DESIGN_ID__)|(__DESIGN_DOC_ID__)/g;
        const segments: React.ReactNode[] = [];
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                const content = text.slice(lastIndex, match.index);
                segments.push(<span key={lastIndex}>{content}</span>);
            }

            if (match[1]) {
                const inner = match[1].replace(/<\/?title>/g, '');
                const codeMatch = inner.match(/Error\s+([\d.]+)/i);
                errorCode = codeMatch?.[1] ?? '';

                segments.push(
                    <ErrorTitle key={match.index} $isWarning={isWarningMessage}>
                        {inner}
                    </ErrorTitle>
                );
            } else if (match[2]) {
                const inner = match[2].replace(/<\/?strong>/g, '');
                segments.push(<StrongText key={match.index}>{inner}</StrongText>);
            } else if (match[3]) {
                const hrefMatch = match[3].match(/href="([^"]*)"/);
                const inlineHref = hrefMatch?.[1] ?? '';
                const inner = match[3].replace(/<a[^>]*>/g, '').replace(/<\/a>/g, '');

                const link = associateGuideSection(errorCode) || inlineHref;

                segments.push(
                    <Link key={match.index} href={link} target='_blank'>
                        {inner}
                    </Link>
                );
            } else if (match[4]) {
                segments.push(<LineBreak key={match.index} />);
            } else if (match[5]) {
                segments.push(
                    <Link key={match.index} href={`mailto:${supportEmail}`}>
                        <Placeholder>{supportEmail}</Placeholder>
                    </Link>
                );
            } else if (match[6]) {
                segments.push(<Placeholder key={match.index}>{designId}</Placeholder>);
            } else if (match[7]) {
                segments.push(<Placeholder key={match.index}>{designDocId}</Placeholder>);
            }

            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < text.length) {
            const remaining = text.slice(lastIndex);
            segments.push(<span key={lastIndex}>{remaining}</span>);
        }

        return segments;
    };

    return (
        <ErrorContainer>
            <StyledErrorCallout isWarning={isWarningMessage}>{renderWithTags(formattedMessage)}</StyledErrorCallout>
        </ErrorContainer>
    );
};

export default OrderErrorRendering;
